import { useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { useIsDarkMode } from "@/hooks/useIsDarkMode";

const Privacy = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isDarkMode = useIsDarkMode();

  return (
    <div className={`min-h-screen flex ${isDarkMode ? 'bg-[#000000] text-white' : 'bg-white text-black'}`}>
      <Sidebar
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        onAuth={() => { }}
        user={null}
        onNewChat={() => { }}
        isDarkMode={isDarkMode}
      />

      <div className="flex-1 lg:ml-16 transition-all duration-300">
        <div className="max-w-[800px] mx-auto px-6 py-20 lg:py-24 font-sans">

          {/* Header */}
          <div className="mb-16 text-center sm:text-left">
            <p className={`text-sm font-medium mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Updated: November 14, 2025
            </p>
            <h1 className="text-5xl sm:text-6xl font-normal tracking-tight mb-2">
              Privacy policy
            </h1>
          </div>

          {/* Content */}
          <div className={`space-y-12 text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>1. Overview</h2>
              <p>
                At Oreog, we value your privacy. This Privacy Policy explains how we collect, use, and share information about you when you use our websites, mobile applications, and other online products and services (collectively, the "Services") or when you otherwise interact with us.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>2. Information We Collect</h2>
              <p>
                We collect information you provide directly to us. This includes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Information:</strong> Your name, email address, and account credentials.</li>
                <li><strong>User Content:</strong> The text prompts, images, and other inputs you post to the Services, and the outputs generated by the AI models.</li>
                <li><strong>Communication Data:</strong> When you contact us for support or other inquiries.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>3. How We Use Your Information</h2>
              <p>
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, maintain, and improve our Services.</li>
                <li>Process your requests and generate AI outputs.</li>
                <li>Analyze usage trends and personalize your experience.</li>
                <li>Communicate with you about updates, security alerts, and support.</li>
                <li>Detect and prevent fraudulent or illegal activity.</li>
              </ul>
              <p>
                We do not sell your personal data to third parties. We may use anonymized or aggregated data to improve our machine learning models.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>4. Use of Third-Party AI Models</h2>
              <p>
                Oreog integrates various third-party AI models (e.g., from OpenAI, Google, Anthropic). When you interact with these models through our platform, your prompts and inputs may be shared with these providers solely for the purpose of generating a response. These providers generally do not use your data for training their models when accessed via our API, but this is subject to their respective policies.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>5. Data Security</h2>
              <p>
                We take reasonable measures to help protect information about you from loss, theft, misuse, and unauthorized access, disclosure, alteration, and destruction. However, no internet transmission is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>6. Your Rights</h2>
              <p>
                Depending on your location, you may have rights regarding your personal data, including the right to access, correct, delete, or export your data. You can manage most of your data directly within your account settings. For other requests, please contact us.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>7. Changes to this Policy</h2>
              <p>
                We may change this Privacy Policy from time to time. If we make changes, we will notify you by revising the date at the top of the policy and, in some cases, providing you with additional notice (such as adding a statement to our homepage or sending you a notification).
              </p>
            </section>

            <section className="space-y-4">
              <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>8. Contact Us</h2>
              <p>
                We aim to respond to all inquiries as promptly as possible. Please note that it may take up to 24–48 hours for us to review and reply to your message.
              </p>

              <p className="font-medium">
                Email: <a href="mailto:talk.oreog@gmail.com" className="underline hover:text-blue-400">talk.oreog@gmail.com</a>
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
